import { referencesEqual } from '@bariendo/lib/services';
import { createReference } from '@medplum/core';
import { Bundle, ExtractResource, OperationOutcome, Resource, ResourceType } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSubscriptionWithLimitHandling } from '../services/subscriptions';

export interface UseLiveDataOptions<T extends Resource = Resource> {
  /**
   * Enable pagination mode. When true, only loads first page initially
   * and provides loadMore function for additional pages.
   * When false (default), loads all data automatically.
   */
  enablePagination?: boolean;
  /**
   * Number of items to load per page. Defaults to 200.
   */
  pageSize?: number;
  /**
   * Whether to add new items to the top of the list instead of the bottom.
   * Defaults to false.
   */
  addNewItemToTop?: boolean;
  /**
   * Disable the subscription updates
   */
  pauseLiveUpdates?: boolean;
  /**
   * Optional callback for subscription updates
   */
  onSubscriptionCallback?: (updatedResource: T) => void;
  /**
   * Optional callback for refresh completion
   */
  onRefreshCallback?: () => void;
  /**
   * Optional delay for live updates in seconds. Defaults to 0.
   */
  liveUpdateDelay?: number;
}

/**
 * Generic hook that returns a live-updating list of FHIR resources for a given search criteria.
 *
 * This hook provides:
 * 1. REST API search execution with automatic or manual pagination
 * 2. Can fetch ALL resources by iterating through pages (default) or load incrementally
 * 3. Real-time updates via WebSocket subscriptions
 * 4. Flexible query customization with search parameters
 * 5. Simple array-based results
 *
 * @param resourceType - The FHIR resource type to search for
 * @param queryParams - Search parameters for the REST API query
 * @param options - Optional configuration for pagination behavior and callbacks
 */
export function useLiveData<RT extends ResourceType, T extends Resource = ExtractResource<RT>>(
  resourceType: RT,
  queryParams: Record<string, any> | undefined,
  options?: UseLiveDataOptions<T>
) {
  const medplum = useMedplum();

  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<Error | OperationOutcome | undefined>();
  const [nextUrl, setNextUrl] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [total, setTotal] = useState<number | undefined>();

  // Extract options with defaults
  const enablePagination = options?.enablePagination ?? false;
  const pageSize = options?.pageSize ?? 200;
  const onSubscriptionCallback = options?.onSubscriptionCallback;
  const onRefreshCallback = options?.onRefreshCallback;
  const addNewItemToTop = options?.addNewItemToTop ?? false;

  // Create a stable key for queryParams to avoid dependency thrash if callers create new objects each render
  const queryKey = useMemo(() => {
    if (!queryParams) return '';

    // Stable stringify: sort keys for deterministic output
    const entries = Object.entries(queryParams).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    const usp = new URLSearchParams(entries as any);

    return usp.toString();
  }, [queryParams]);

  const queryParamsRef = useRef<Record<string, any> | undefined>(queryParams);
  useEffect(() => {
    // Update current params whenever effective content changes
    queryParamsRef.current = queryParams;
  }, [queryKey, queryParams]);

  const processError = useCallback((err: Error | OperationOutcome | undefined) => {
    if (err && typeof err === 'object' && 'resourceType' in err && err.resourceType === 'OperationOutcome') {
      setError(err as OperationOutcome);
    } else if (err instanceof Error) {
      setError(err);
    } else {
      setError(new Error('Unknown error occurred while fetching data'));
    }
  }, []);

  // Helper function to extract next URL from bundle links
  const getNextUrlFromBundle = useCallback((bundle: Bundle): string | undefined => {
    return bundle.link?.find((link) => link.relation === 'next')?.url;
  }, []);

  // Common function to fetch a single page of data
  const fetchPage = useCallback(
    async (offset: number): Promise<{ data: T[]; nextUrl?: string; total?: number }> => {
      const searchParams = {
        ...(queryParamsRef.current ?? {}),
        _count: pageSize.toString(),
        _offset: offset.toString(),
        _total: 'accurate',
      };

      const bundle = await medplum.search(resourceType, searchParams);
      const results = bundle.entry?.map((entry: any) => entry.resource) || [];
      const data = Array.isArray(results) ? (results as unknown as T[]) : [];
      const nextUrl = getNextUrlFromBundle(bundle);
      const total = bundle.total;

      return { data, nextUrl, total };
    },
    [medplum, resourceType, pageSize, getNextUrlFromBundle]
  );

  // Function to fetch data using a next URL from bundle links
  const fetchPageByUrl = useCallback(
    async (url: string): Promise<{ data: T[]; nextUrl?: string; total?: number }> => {
      const bundle = await medplum.get(url);
      const results = bundle.entry?.map((entry: any) => entry.resource) || [];
      const data = Array.isArray(results) ? (results as unknown as T[]) : [];
      const nextUrl = getNextUrlFromBundle(bundle);
      const total = bundle.total;

      return { data, nextUrl, total };
    },
    [medplum, getNextUrlFromBundle]
  );

  const fetchData = useCallback(
    async (isRefresh = false) => {
      if (queryParamsRef.current === undefined) {
        setLoading(false);
        return;
      }

      try {
        if (isRefresh) {
          setRefreshing(true);
          setNextUrl(undefined);
          setTotal(undefined);
          setHasMore(true);
        } else {
          setLoading(true);
        }
        setError(undefined);

        if (enablePagination) {
          // Pagination mode: load only first page
          const pageResult = await fetchPage(0);
          setData(pageResult.data);
          setNextUrl(pageResult.nextUrl);
          setTotal(pageResult.total);
          setHasMore(!!pageResult.nextUrl);
        } else {
          // Load all mode: iterate through all pages
          const allResults: T[] = [];
          let offset = 0;
          let totalCount: number | undefined;

          while (true) {
            const pageResult = await fetchPage(offset);
            allResults.push(...pageResult.data);
            totalCount = pageResult.total;

            if (!pageResult.nextUrl) {
              break;
            }

            offset += pageSize;
          }

          setData(allResults);
          setTotal(totalCount);
        }
      } catch (err: any) {
        processError(err);
      } finally {
        if (isRefresh) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [fetchPage, processError, enablePagination, pageSize]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // When the effective query changes, refetch (without recreating callbacks unnecessarily)
  useEffect(() => {
    fetchData();
  }, [queryKey, fetchData]);

  const loadMore = useCallback(async () => {
    if (!enablePagination || loadingMore || !hasMore || !nextUrl) {
      return;
    }

    try {
      setLoadingMore(true);
      setError(undefined);

      const pageResult = await fetchPageByUrl(nextUrl);

      // Append new results to existing data
      setData((prevData) => [...prevData, ...pageResult.data]);
      setNextUrl(pageResult.nextUrl);
      // Update total if it's provided (should be consistent, but just in case)
      if (pageResult.total !== undefined) {
        setTotal(pageResult.total);
      }
      setHasMore(!!pageResult.nextUrl);
    } catch (err: any) {
      processError(err);
    } finally {
      setLoadingMore(false);
    }
  }, [enablePagination, loadingMore, hasMore, nextUrl, fetchPageByUrl, processError]);

  const updateDataWithResource = useCallback(
    (updatedResource: T) => {
      setData((prevData) => {
        const existingIndex = prevData.findIndex((r) =>
          referencesEqual(createReference(r), createReference(updatedResource))
        );

        if (existingIndex !== -1) {
          const updatedData = [...prevData];
          updatedData[existingIndex] = updatedResource;
          return updatedData;
        }

        return addNewItemToTop ? [updatedResource, ...prevData] : [...prevData, updatedResource];
      });

      onSubscriptionCallback?.(updatedResource);
    },
    [onSubscriptionCallback, addNewItemToTop]
  );

  const handleSubscriptionUpdate = useCallback(
    async (bundle: Bundle) => {
      if (options?.liveUpdateDelay) {
        await new Promise((resolve) => setTimeout(resolve, options.liveUpdateDelay! * 1000));
      }

      const updatedResource = bundle.entry?.[1]?.resource as T | undefined;

      if (!updatedResource || !updatedResource.id) {
        return;
      }

      if (Object.keys(queryParamsRef.current ?? {}).includes('_include')) {
        // If the query includes _include, we will fetch the resource again using
        // searchResources so we can also update the related resources
        const resources = await medplum.searchResources(resourceType, {
          _id: updatedResource.id,
          ...(queryParamsRef.current ?? {}),
        });
        resources.forEach((resource) => updateDataWithResource(resource as any));
      } else {
        updateDataWithResource(updatedResource);
      }
    },
    [updateDataWithResource, options?.liveUpdateDelay, medplum, resourceType, queryParamsRef]
  );

  const subscriptionCriteria = useMemo(() => {
    if (options?.pauseLiveUpdates) return '';
    return queryKey ? `${resourceType}?${queryKey}` : '';
  }, [resourceType, queryKey, options?.pauseLiveUpdates]);

  useSubscriptionWithLimitHandling(subscriptionCriteria, handleSubscriptionUpdate);

  const refresh = useCallback(() => {
    fetchData(true);
    onRefreshCallback?.();
  }, [fetchData, onRefreshCallback]);

  return {
    data,
    total,
    loading,
    refreshing,
    error,
    refresh,
    // Pagination-specific properties
    loadMore,
    loadingMore,
    hasMore,
  } as const;
}
